import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  ExpandMore,
  Save,
  Refresh,
  Add,
  Delete,
  TrendingUp,
} from '@mui/icons-material';
import AlertMessageDialog from '../../../components/Dialogs/alertMessageDialog';

const ScoringTab = () => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [alertDialog, setAlertDialog] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const idcompany = localStorage.getItem('idcompany');
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/scoring/config/${idcompany}`, {
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': idcompany
        }
      });

      if (response.ok) {
        const data = await response.json();
        setConfig(data);
      } else {
        throw new Error('Error al cargar configuración');
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      setAlertDialog({ open: true, message: 'Error al cargar configuración', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    // Validate weights sum to 100
    if (Math.abs(config.peso_historial + config.peso_cedula - 100) > 0.01) {
      setAlertDialog({ open: true, message: 'Los pesos principales deben sumar 100%', severity: 'error' });
      return;
    }

    // Validate historial metrics weights
    const historialTotal = Object.values(config.configuracion_historial).reduce(
      (sum, metric) => sum + (metric.peso || 0), 0
    );
    if (Math.abs(historialTotal - 100) > 0.01) {
      setAlertDialog({ open: true, message: 'Los pesos de las métricas de historial deben sumar 100%', severity: 'error' });
      return;
    }

    setLoading(true);
    try {
      const idcompany = localStorage.getItem('idcompany');
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/scoring/config/${idcompany}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Usuario-ID': localStorage.getItem('id'),
          'X-company-ID': idcompany
        },
        body: JSON.stringify(config)
      });

      if (response.ok) {
        setAlertDialog({ open: true, message: 'Configuración guardada exitosamente', severity: 'success' });
      } else {
        const error = await response.json();
        throw new Error(error.error || 'Error al guardar configuración');
      }
    } catch (error) {
      console.error('Error saving config:', error);
      setAlertDialog({ open: true, message: error.message || 'Error al guardar configuración', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateMetricConfig = (metricKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      configuracion_historial: {
        ...prev.configuracion_historial,
        [metricKey]: {
          ...prev.configuracion_historial[metricKey],
          [field]: value
        }
      }
    }));
  };

  const updateRange = (metricKey, rangeIndex, field, value) => {
    setConfig(prev => ({
      ...prev,
      configuracion_historial: {
        ...prev.configuracion_historial,
        [metricKey]: {
          ...prev.configuracion_historial[metricKey],
          rangos: prev.configuracion_historial[metricKey].rangos.map((rango, idx) =>
            idx === rangeIndex ? { ...rango, [field]: value } : rango
          )
        }
      }
    }));
  };

  const addRange = (metricKey) => {
    setConfig(prev => ({
      ...prev,
      configuracion_historial: {
        ...prev.configuracion_historial,
        [metricKey]: {
          ...prev.configuracion_historial[metricKey],
          rangos: [
            ...prev.configuracion_historial[metricKey].rangos,
            { min: 0, max: 100, puntos: 0 }
          ]
        }
      }
    }));
  };

  const removeRange = (metricKey, rangeIndex) => {
    setConfig(prev => ({
      ...prev,
      configuracion_historial: {
        ...prev.configuracion_historial,
        [metricKey]: {
          ...prev.configuracion_historial[metricKey],
          rangos: prev.configuracion_historial[metricKey].rangos.filter((_, idx) => idx !== rangeIndex)
        }
      }
    }));
  };

  if (loading && !config) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography>Cargando configuración...</Typography>
      </Box>
    );
  }

  if (!config) {
    return (
      <Alert severity="warning">
        No se pudo cargar la configuración. Por favor, intenta de nuevo.
      </Alert>
    );
  }

  const metricLabels = {
    pagos_puntuales: 'Pagos Puntuales',
    prestamos_completados: 'Préstamos Completados',
    mora: 'Mora (Días de Atraso)',
    antiguedad: 'Antigüedad (Meses)',
    volumen: 'Volumen (Cantidad de Préstamos)',
  };

  const metricDescriptions = {
    pagos_puntuales: 'Porcentaje de pagos realizados puntualmente',
    prestamos_completados: 'Porcentaje de préstamos completados exitosamente',
    mora: 'Días máximos de atraso en pagos',
    antiguedad: 'Tiempo de relación con el cliente en meses',
    volumen: 'Cantidad total de préstamos del cliente',
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600} color="text.primary">
            Configuración de Scoring de Clientes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configura los pesos y parámetros para el cálculo del score de crédito
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchConfig}
            disabled={loading}
          >
            Recargar
          </Button>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={saveConfig}
            disabled={loading}
          >
            Guardar Cambios
          </Button>
        </Stack>
      </Box>

      {/* Main Weights */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Pesos Principales
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Los pesos deben sumar exactamente 100%
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Peso de Historial Interno (%)"
                type="number"
                fullWidth
                value={config.peso_historial}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setConfig(prev => ({
                    ...prev,
                    peso_historial: val,
                    peso_cedula: 100 - val
                  }));
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText={`Restante: ${(100 - config.peso_historial).toFixed(2)}%`}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Peso de Validación de Cédula (%)"
                type="number"
                fullWidth
                value={config.peso_cedula}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setConfig(prev => ({
                    ...prev,
                    peso_cedula: val,
                    peso_historial: 100 - val
                  }));
                }}
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                helperText={`Restante: ${(100 - config.peso_cedula).toFixed(2)}%`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Cédula Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Configuración de Validación de Cédula
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Puntos si Cédula Válida"
                type="number"
                fullWidth
                value={config.configuracion_cedula.puntos_valida}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  configuracion_cedula: {
                    ...prev.configuracion_cedula,
                    puntos_valida: parseInt(e.target.value) || 0
                  }
                }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Puntos si Cédula Inválida"
                type="number"
                fullWidth
                value={config.configuracion_cedula.puntos_invalida}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  configuracion_cedula: {
                    ...prev.configuracion_cedula,
                    puntos_invalida: parseInt(e.target.value) || 0
                  }
                }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Puntos si No Validada"
                type="number"
                fullWidth
                value={config.configuracion_cedula.puntos_no_validada}
                onChange={(e) => setConfig(prev => ({
                  ...prev,
                  configuracion_cedula: {
                    ...prev.configuracion_cedula,
                    puntos_no_validada: parseInt(e.target.value) || 0
                  }
                }))}
                inputProps={{ min: 0, max: 100 }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Historial Metrics Configuration */}
      <Card>
        <CardContent>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
            Configuración de Métricas de Historial
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Los pesos de las métricas deben sumar exactamente 100%
          </Alert>

          {Object.entries(config.configuracion_historial).map(([metricKey, metric]) => (
            <Accordion key={metricKey} sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {metricLabels[metricKey]}
                  </Typography>
                  <Chip label={`Peso: ${metric.peso}%`} size="small" color="primary" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {metricDescriptions[metricKey]}
                </Typography>

                <TextField
                  label="Peso de la Métrica (%)"
                  type="number"
                  fullWidth
                  value={metric.peso}
                  onChange={(e) => updateMetricConfig(metricKey, 'peso', parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  sx={{ mb: 3 }}
                />

                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Rangos y Puntos
                </Typography>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Mínimo</TableCell>
                        <TableCell>Máximo</TableCell>
                        <TableCell>Puntos</TableCell>
                        <TableCell>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {metric.rangos.map((rango, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={rango.min !== undefined ? rango.min : rango.min_dias !== undefined ? rango.min_dias : rango.min_meses !== undefined ? rango.min_meses : rango.min_prestamos !== undefined ? rango.min_prestamos : ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (metricKey === 'mora') {
                                  updateRange(metricKey, idx, 'min_dias', val);
                                } else if (metricKey === 'antiguedad') {
                                  updateRange(metricKey, idx, 'min_meses', val);
                                } else if (metricKey === 'volumen') {
                                  updateRange(metricKey, idx, 'min_prestamos', val);
                                } else {
                                  updateRange(metricKey, idx, 'min', val);
                                }
                              }}
                              inputProps={{ step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={rango.max !== undefined ? rango.max : rango.max_dias !== undefined ? rango.max_dias : rango.max_meses !== undefined ? rango.max_meses : rango.max_prestamos !== undefined ? rango.max_prestamos : ''}
                              onChange={(e) => {
                                const val = parseFloat(e.target.value) || 0;
                                if (metricKey === 'mora') {
                                  updateRange(metricKey, idx, 'max_dias', val);
                                } else if (metricKey === 'antiguedad') {
                                  updateRange(metricKey, idx, 'max_meses', val);
                                } else if (metricKey === 'volumen') {
                                  updateRange(metricKey, idx, 'max_prestamos', val);
                                } else {
                                  updateRange(metricKey, idx, 'max', val);
                                }
                              }}
                              inputProps={{ step: 0.01 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              type="number"
                              size="small"
                              value={rango.puntos}
                              onChange={(e) => updateRange(metricKey, idx, 'puntos', parseInt(e.target.value) || 0)}
                              inputProps={{ min: 0, max: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeRange(metricKey, idx)}
                              disabled={metric.rangos.length === 1}
                            >
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Button
                  startIcon={<Add />}
                  onClick={() => addRange(metricKey)}
                  sx={{ mt: 1 }}
                  size="small"
                >
                  Agregar Rango
                </Button>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      <AlertMessageDialog
        open={alertDialog.open}
        message={alertDialog.message}
        severity={alertDialog.severity}
        handleClose={() => setAlertDialog({ ...alertDialog, open: false })}
      />
    </Box>
  );
};

export default ScoringTab;

